import React from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Alert,
  AlertTitle,
  CircularProgress,
} from "@mui/material";
import {
  CheckCircle as ReviewIcon,
  Settings as ConfigIcon,
  Code as CodeIcon,
  Download as ExportIcon,
  Visibility as PreviewIcon,
} from "@mui/icons-material";

const ReviewComponent = ({
  formValues,
  ageRanges,
  ajvSchemas,
  ingestionMappings,
  prospectMappings,
  enrichConsumerMappings,
  exportStatus,
  onPreviewProspectMapping,
  onExportConfiguration,
}) => {
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
        Configuration Review
      </Typography>
      <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
        Review your complete configuration before saving. Verify all mappings and settings are
        correct.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>Final Review</AlertTitle>
        Please carefully review all configuration settings below before proceeding to save.
      </Alert>

      <Grid container spacing={3}>
        <Grid size={6}>
          <Card elevation={2} sx={{ height: "100%" }}>
            <CardHeader
              avatar={<ConfigIcon color="primary" />}
              title="Configuration Details"
              subheader="Core settings and parameters"
            />
            <Divider />
            <CardContent>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography>
                  <strong>Partner Name:</strong> {formValues.partnerName || "Not set"}
                </Typography>
                <Typography>
                  <strong>Hash Identifier:</strong> {formValues.hashIdentifier}
                </Typography>
                <Typography>
                  <strong>Country Header:</strong> {formValues.countryAttribute || "Not set"}
                </Typography>
                <Typography>
                  <strong>DOB Header:</strong> {formValues.dobAttribute || "Not set"}
                </Typography>
                <Typography>
                  <strong>Age Ranges:</strong> {Object.keys(ageRanges).length} configured
                </Typography>
                <Typography>
                  <strong>Schema Markets:</strong> {Object.keys(ajvSchemas).length} configured
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={6}>
          <Card elevation={2} sx={{ height: "100%" }}>
            <CardHeader
              avatar={<CodeIcon color="primary" />}
              title="Mapper Summary"
              subheader="Field mappings and transformations"
            />
            <Divider />
            <CardContent>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography>
                  <strong>Ingestion Mappings:</strong>{" "}
                  {Object.values(ingestionMappings).filter((m) => m.include).length} active
                </Typography>
                <Typography>
                  <strong>Prospect Mappings:</strong> {prospectMappings.length} configured
                </Typography>
                <Typography>
                  <strong>Enrich Consumer:</strong> {enrichConsumerMappings.length} configured
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card elevation={2} sx={{ mt: 3 }}>
        <CardHeader
          avatar={<ReviewIcon color="success" />}
          title="Export Configuration"
          subheader="Preview and export your configuration settings"
        />
        <Divider />
        <CardContent>
          {/* Export Status Feedback */}
          {exportStatus.success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <AlertTitle>Success!</AlertTitle>
              Configuration saved successfully to backend!
            </Alert>
          )}
          {exportStatus.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>Export Failed</AlertTitle>
              {exportStatus.error}
            </Alert>
          )}

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              onClick={onPreviewProspectMapping}
              size="large"
              startIcon={<PreviewIcon />}
            >
              Preview Prospect Mapping
            </Button>
            <Button
              variant="contained"
              onClick={onExportConfiguration}
              size="large"
              startIcon={exportStatus.loading ? <CircularProgress size={20} /> : <ExportIcon />}
              color="primary"
              disabled={exportStatus.loading}
            >
              {exportStatus.loading ? "Saving..." : "Save Configuration"}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ReviewComponent;
